import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface LogFile {
  name: string;
  size: number;
  date: string;
}

interface LogFileManagerProps {
  password: string;
}

const LogFileManager: React.FC<LogFileManagerProps> = ({ password }) => {
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    fetchLogFiles();
  }, []);

  const fetchLogFiles = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/dominant-monitoring/log-files`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setLogFiles(response.data.files);
        setSelectedFiles([]);
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to fetch log files",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFile = (fileName: string) => {
    setSelectedFiles((prev) =>
      prev.includes(fileName)
        ? prev.filter((f) => f !== fileName)
        : [...prev, fileName]
    );
  };

  const handleToggleAll = () => {
    if (selectedFiles.length === logFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(logFiles.map((f) => f.name));
    }
  };

  const handleDelete = async () => {
    if (selectedFiles.length === 0) {
      setMessage({
        type: "error",
        text: "No files selected for deletion",
      });
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedFiles.length} log file(s)? This action cannot be undone!`
      )
    ) {
      return;
    }

    setDeleting(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${API_BASE_URL}/api/dominant-monitoring/log-files`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
          data: {
            files: selectedFiles,
          },
        }
      );

      if (response.data.success) {
        setMessage({
          type: "success",
          text: response.data.message,
        });
        fetchLogFiles(); // Refresh the list
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to delete log files",
      });
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-red-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-red-700">🗑️ Danger Zone</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage and delete log files
          </p>
        </div>
        <button
          onClick={fetchLogFiles}
          disabled={loading}
          className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
          title="Refresh file list"
        >
          <svg
            className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-md ${
            message.type === "success"
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <p
            className={`text-sm ${
              message.type === "success" ? "text-green-800" : "text-red-800"
            }`}
          >
            {message.text}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {logFiles.length} log file(s) • {selectedFiles.length} selected
        </div>
        <button
          onClick={handleDelete}
          disabled={deleting || selectedFiles.length === 0}
          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {deleting
            ? "Deleting..."
            : `Delete Selected (${selectedFiles.length})`}
        </button>
      </div>

      {/* Log Files List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading files...</div>
      ) : logFiles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No log files found.</p>
          <p className="text-sm mt-1">
            Log files will appear here when anomalies are logged.
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Select All Header */}
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFiles.length === logFiles.length}
                onChange={handleToggleAll}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">
                Select All
              </span>
            </label>
          </div>

          {/* File List */}
          <div className="max-h-96 overflow-y-auto">
            {logFiles.map((file) => (
              <label
                key={file.name}
                className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedFiles.includes(file.name)}
                  onChange={() => handleToggleFile(file.name)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Date: {file.date}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogFileManager;
